class VideoWrapper extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector("video");
    this.autoplay = this.dataset.autoplay;
    this.setupControls();
    this.setupAutoplayObserver();
  }

  setupControls() {
    if (!this.video) return;

    this.playButton = this.querySelector(".video--play, .video-toggle-play");
    this.muteButton = this.querySelector(".video--mute, .video-toggle-mute");
    this.pipButton = this.querySelector(".video--pip");

    if (
      this.playButton &&
      !this.playButton.classList.contains("listener-attached")
    ) {
      this.playButton.classList.add("listener-attached");
      this.playButton.addEventListener("click", this.togglePlay.bind(this));
    }

    if (
      this.muteButton &&
      !this.muteButton.classList.contains("listener-attached")
    ) {
      this.muteButton.classList.add("listener-attached");
      this.muteButton.addEventListener("click", this.toggleMute.bind(this));
    }

    if (
      this.pipButton &&
      !this.pipButton.classList.contains("listener-attached")
    ) {
      this.pipButton.classList.add("listener-attached");
      this.pipButton.addEventListener("click", this.pipVideo.bind(this));
    }
  }

  setupAutoplayObserver() {
    if (!this.video || this.autoplay !== "true") return;

    this.autoplayObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAutoplayed) {
            this.hasAutoplayed = true;

            this.video
              .play()
              .then(() => {
                this.playButton?.classList.add("playing");
              })
              .catch(() => {
                this.playButton?.classList.remove("playing");
              });

            this.autoplayObserver.unobserve(this);
          }
        });
      },
      {
        threshold: 0.5, // play when 50% visible
      }
    );

    this.autoplayObserver.observe(this);
  }

  togglePlay(e) {
    e.stopPropagation();

    if (this.video.paused) {
      this.playButton.classList.add("playing");
      this.video.play().catch(() => {
        this.playButton?.classList.remove("playing");
      });
    } else {
      this.playButton.classList.remove("playing");
      this.video.pause();
    }
  }

  toggleMute(e) {
    e.stopPropagation();
    this.video.muted = !this.video.muted;
    this.muteButton.classList.toggle("muted", this.video.muted);
  }

  pipVideo(e) {
    e.stopPropagation();
    e.preventDefault();

    this.video.pause();
    this.playButton.classList.remove("playing");
    this.video.pause();
    this.showPiP();
  }

  showPiP() {
    document.querySelector(".pip-gallery")?.remove();
    document.body.insertAdjacentHTML(
      "beforeend",
      `
<div class="pip-gallery is-mute expanded">
  <div class="pip-gallery--wrapper">
    <button class="close-pip-gallery"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path d="M 38.982422 6.9707031 A 2.0002 2.0002 0 0 0 37.585938 7.5859375 L 24 21.171875 L 10.414062 7.5859375 A 2.0002 2.0002 0 0 0 8.9785156 6.9804688 A 2.0002 2.0002 0 0 0 7.5859375 10.414062 L 21.171875 24 L 7.5859375 37.585938 A 2.0002 2.0002 0 1 0 10.414062 40.414062 L 24 26.828125 L 37.585938 40.414062 A 2.0002 2.0002 0 1 0 40.414062 37.585938 L 26.828125 24 L 40.414062 10.414062 A 2.0002 2.0002 0 0 0 38.982422 6.9707031 z"/></svg></button>
    <button aria-label="Mudo/Som" class="pip-gallery--mute">
      <span class="mute">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          class="icon icon-mute" 
        >
          <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM17.78 9.22a.75.75 0 1 0-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 1 0 1.06-1.06L20.56 12l1.72-1.72a.75.75 0 1 0-1.06-1.06l-1.72 1.72-1.72-1.72Z" />
        </svg>
      </span>
      <span class="unmute">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          class="icon icon-unmute"
        >
          <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 0 0 1.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06ZM18.584 5.106a.75.75 0 0 1 1.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 0 1-1.06-1.06 8.25 8.25 0 0 0 0-11.668.75.75 0 0 1 0-1.06Z" />
          <path d="M15.932 7.757a.75.75 0 0 1 1.061 0 6 6 0 0 1 0 8.486.75.75 0 0 1-1.06-1.061 4.5 4.5 0 0 0 0-6.364.75.75 0 0 1 0-1.06Z" />
        </svg>
      </span>
    </button>
    <div class="pip-gallery-carousel">
    </div>
  </div>
</div>
      `
    );

    const gallery = document.querySelector(".pip-gallery");
    const carousel = document.querySelector(".pip-gallery-carousel");

    const videoWrapper = document.createElement("div");
    videoWrapper.classList.add("video-wrapper");

    const videoEl = this.video.cloneNode(true);

    const playBtn = document.createElement("button");
    playBtn.classList.add("play-wrapper");
    playBtn.insertAdjacentHTML(
      "beforeend",
      `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="icon icon-play">
  <path fill-rule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clip-rule="evenodd"></path>
</svg> 
        `
    );

    playBtn.addEventListener("click", function () {
      if (
        window.innerWidth > 1024 ||
        (window.innerWidth <= 1024 && gallery.classList.contains("expanded"))
      ) {
        videoWrapper.classList.toggle("playing");
        if (videoEl.paused) {
          videoEl.play();
        } else {
          videoEl.pause();
        }
      }
    });

    videoWrapper.appendChild(videoEl);
    videoWrapper.appendChild(playBtn);

    carousel.appendChild(videoWrapper);

    videoWrapper.classList.add("playing");
    videoWrapper.classList.add("current");
    videoEl.play();
    videoEl.muted = true;

    const mute = document.querySelector(".pip-gallery .pip-gallery--mute");

    mute.addEventListener("click", function () {
      gallery.classList.toggle("is-mute");
      const currVideo = carousel.querySelector(".video-wrapper.current");
      currVideo.querySelector("video").muted =
        gallery.classList.contains("is-mute");
    });

    function shrinkGallery(event) {
      if (
        !gallery.contains(event.target) &&
        !this.contains(event.target) &&
        window.innerWidth <= 1024
      ) {
        gallery.classList.remove("expanded");
      }
    }

    const closeBtn = document.querySelector(".close-pip-gallery");
    closeBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      document.querySelector(".pip-gallery")?.remove();
      document.removeEventListener("click", shrinkGallery.bind(this));
    });

    gallery.addEventListener("click", function () {
      if (window.innerWidth <= 1024) {
        gallery.classList.add("expanded");
      }
    });

    gallery
      .querySelectorAll(".product-form__submit, .video-share")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const currVideo = carousel.querySelector(".video-wrapper.current");
          currVideo.classList.remove("playing");
          currVideo.querySelector("video").pause();
        });
      });

    document.addEventListener("click", shrinkGallery.bind(this));
  }
}

customElements.define("video-wrapper", VideoWrapper);
